import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Compass, Sparkles, Crown, Lock, ArrowRight } from 'lucide-react';
import { Hint } from '@/components/ui/hint';
import { HELP } from '@/lib/helpText';
import { SetupStepper } from '@/components/study/SetupStepper';

const MODE_HELP = {
  explore: HELP.study.modeExplore,
  ai_tutor: HELP.study.modeAiTutor,
};

const ICONS = { compass: Compass, sparkles: Sparkles, crown: Crown };

/** Mode chooser. Step 1 of first-run setup — it cannot be escaped until a mode
 *  is chosen. Afterwards it reappears only when the student taps the mode
 *  button to change it. Two choices, never three. */
export function ModePickerDialog({ open, modes, onPick, onClose, studentName, blocking = false, step = 0, totalSteps = 5 }) {
  const navigate = useNavigate();
  const firstCardRef = useRef(null);
  const lockedMode = (modes || []).find(m => m.locked);

  // Default (Explore) card owns the first tab stop.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => firstCardRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, [open, modes]);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !blocking) onClose?.(); }}>
      <DialogContent
        className="max-w-3xl rounded-3xl p-0 overflow-hidden border-none"
        data-testid="mode-picker-dialog"
        hideClose={blocking}
        onEscapeKeyDown={e => { if (blocking) e.preventDefault(); }}
        onPointerDownOutside={e => { if (blocking) e.preventDefault(); }}
        onInteractOutside={e => { if (blocking) e.preventDefault(); }}
      >
        <div className="px-6 sm:px-8 pt-7 pb-2 text-center">
          {blocking && <div className="mb-3"><SetupStepper step={step} total={totalSteps} /></div>}
          <DialogTitle className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
            Mode Of Study
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500 font-body mt-1.5">
            {studentName ? `${studentName}, choose` : 'Choose'} Explore Mode or Exam Mode.
          </DialogDescription>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-5 sm:p-6">
          {(modes || []).map((m, i) => {
            const Icon = ICONS[m.icon] || Compass;
            return (
              <Hint key={m.key} text={m.locked ? HELP.study.modeLocked : MODE_HELP[m.key]} side="bottom" className="w-full">
              <button
                ref={i === 0 ? firstCardRef : null}
                data-testid={`mode-card-${m.key}`}
                onClick={() => (m.locked ? navigate('/subscription') : onPick?.(m.key))}
                style={{ animationDelay: `${i * 70}ms` }}
                className={`group relative overflow-hidden rounded-2xl p-4 w-full text-left text-white bg-gradient-to-br ${m.gradient} shadow-md hover:shadow-xl transition-all hover:-translate-y-1 active:scale-[0.98] animate-slide-up ${m.locked ? 'saturate-[0.85]' : ''}`}
              >
                <div className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-white/15 blur-xl" />
                <div className="relative">
                  <div className="flex items-start justify-between gap-2">
                    <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </div>
                    {m.locked ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-heading uppercase tracking-wider bg-white/90 text-slate-900 px-2 py-1 rounded-full">
                        <Lock className="w-3 h-3" /> {m.upgrade_label || 'Premium'}
                      </span>
                    ) : m.is_default ? (
                      <span className="text-[10px] font-heading uppercase tracking-wider bg-white/20 px-2 py-1 rounded-full">Default</span>
                    ) : null}
                  </div>
                  <p className="font-heading text-base font-bold mt-3">{m.name}</p>
                  <p className="text-xs text-white/90 font-body leading-relaxed mt-1">{m.tagline}</p>
                  <span className="inline-flex items-center gap-1 text-[11px] font-heading mt-3 opacity-90 group-hover:opacity-100">
                    {m.locked ? 'Unlock with Premium' : 'Start here'} <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </button>
              </Hint>
            );
          })}
        </div>
        {lockedMode && (
          <div className="mx-5 sm:mx-6 -mt-2 mb-3 rounded-2xl bg-slate-50 ring-1 ring-slate-200 px-4 py-3 text-center">
            <p className="text-[12px] font-body text-slate-600" data-testid="mode-premium-note">
              You’re on a plan that includes <span className="font-heading text-slate-900">Explore Mode</span>.
              {' '}{lockedMode.name} — step-by-step exam prep — is part of Premium.
            </p>
            <button type="button" data-testid="mode-upgrade-link"
              onClick={() => navigate('/subscription')}
              className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-heading text-indigo-600 hover:text-indigo-700 hover:underline">
              Go Premium to unlock {lockedMode.name} <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        )}
        <p className="text-[11px] text-slate-400 font-body text-center pb-5 px-6">
          Your Mode Of Study is saved until you change it.
        </p>
      </DialogContent>
    </Dialog>
  );
}

export default ModePickerDialog;
